import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, Typography, CircularProgress, Switch,
    Select, MenuItem, FormControl, InputLabel, Chip, Alert,
    Snackbar, Accordion, AccordionSummary, AccordionDetails, IconButton,
    Autocomplete
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    getAllFeatureFlagsV2, setFeatureFlagV2, deleteFeatureFlagV2,
    getSubscriptionTiersV2, getAdminUsersV2
} from '../../api/admin';
import { useFeatureFlags } from '../../context/FeatureFlagContext';
import logger from '../../utils/logger';
import './AdminView.css';

// Known flags with metadata
const KNOWN_FLAGS = [
    { id: 'chat', name: 'Chat', description: 'AI Chat panel' },
    { id: 'journal', name: 'Journal', description: 'Trading journal (save button & history)' },
    { id: 'journalPage', name: 'Journal Page', description: 'Trading journal page access' },
    { id: 'crypto', name: 'Crypto', description: 'Cryptocurrency assets' },
    { id: 'stocks', name: 'Stocks', description: 'Stocks/Equities assets' },
    { id: 'llm_rules', name: 'LLM Rules', description: 'AI validation rules' },
    { id: 'analytics_llm_rules', name: 'Analytics LLM Rules', description: 'AI-powered analytics modules' },
];

const FeatureFlagsTab = ({ auth }) => {
    const { refreshFlags } = useFeatureFlags();
    const [loading, setLoading] = useState(false);
    const [flagsData, setFlagsData] = useState({ global: [], tierOverrides: [], userOverrides: [] });
    const [tiers, setTiers] = useState([]);
    const [users, setUsers] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Dialog states
    const [addGlobalDialog, setAddGlobalDialog] = useState(false);
    const [addTierDialog, setAddTierDialog] = useState(false);
    const [addUserDialog, setAddUserDialog] = useState(false);

    // Form states
    const [selectedTier, setSelectedTier] = useState('');
    const [selectedFlag, setSelectedFlag] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [flagValue, setFlagValue] = useState('true');
    const [customFlagId, setCustomFlagId] = useState('');
    const [helpDialog, setHelpDialog] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [flagsResponse, tiersResponse, usersResponse] = await Promise.all([
                new Promise((resolve) => getAllFeatureFlagsV2(auth, resolve)),
                new Promise((resolve) => getSubscriptionTiersV2(auth, resolve)),
                new Promise((resolve) => getAdminUsersV2(auth, resolve)),
            ]);

            if (flagsResponse && !flagsResponse.error) {
                setFlagsData(flagsResponse);
            }
            if (Array.isArray(tiersResponse)) {
                setTiers(tiersResponse);
            }
            if (Array.isArray(usersResponse)) {
                setUsers(usersResponse);
            }
        } catch (error) {
            logger.error('Error loading data:', error);
            setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSetFlag = async (key, value, description = 'Feature flag') => {
        setLoading(true);
        try {
            const response = await new Promise((resolve) => {
                setFeatureFlagV2(auth, resolve, key, value, description);
            });
            if (response && !response.error) {
                setSnackbar({ open: true, message: 'Flag saved', severity: 'success' });
                loadData();
                if (refreshFlags) refreshFlags();
            } else {
                throw new Error(response?.error || 'Failed to save');
            }
        } catch (error) {
            logger.error('Error setting flag:', error);
            setSnackbar({ open: true, message: 'Failed to save flag', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFlag = async (key) => {
        if (!window.confirm(`Delete this override?`)) return;

        setLoading(true);
        try {
            const response = await new Promise((resolve) => {
                deleteFeatureFlagV2(auth, resolve, key);
            });
            if (response && !response.error) {
                setSnackbar({ open: true, message: 'Deleted', severity: 'success' });
                loadData();
                if (refreshFlags) refreshFlags();
            }
        } catch (error) {
            logger.error('Error deleting flag:', error);
            setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Add handlers
    const handleAddGlobal = async () => {
        const flagId = selectedFlag === 'custom' ? customFlagId : selectedFlag;
        if (!flagId) {
            setSnackbar({ open: true, message: 'Select a flag', severity: 'error' });
            return;
        }
        const key = `feature:${flagId}`;
        const flag = KNOWN_FLAGS.find(f => f.id === flagId);
        await handleSetFlag(key, flagValue, flag?.description || 'Feature flag');
        setAddGlobalDialog(false);
        resetForm();
    };

    const handleAddTierOverride = async () => {
        const flagId = selectedFlag === 'custom' ? customFlagId : selectedFlag;
        if (!flagId || !selectedTier) {
            setSnackbar({ open: true, message: 'Select flag and tier', severity: 'error' });
            return;
        }
        const key = `feature:${flagId}:tier:${selectedTier}`;
        await handleSetFlag(key, flagValue, `Tier override for ${selectedTier}`);
        setAddTierDialog(false);
        resetForm();
    };

    const handleAddUserOverride = async () => {
        const flagId = selectedFlag === 'custom' ? customFlagId : selectedFlag;
        if (!flagId || !selectedUser) {
            setSnackbar({ open: true, message: 'Select flag and user', severity: 'error' });
            return;
        }
        const key = `feature:${flagId}:user:${selectedUser.userId}`;
        await handleSetFlag(key, flagValue, `Override for ${selectedUser.email || selectedUser.name}`);
        setAddUserDialog(false);
        resetForm();
    };

    const resetForm = () => {
        setSelectedFlag('');
        setCustomFlagId('');
        setFlagValue('true');
        setSelectedUser(null);
    };

    // Helpers
    const extractFlagId = (key) => {
        const parts = key.replace('feature:', '').split(':');
        return parts[0];
    };

    const extractScopeId = (key) => {
        const parts = key.split(':');
        if (key.includes(':tier:')) return parts[parts.indexOf('tier') + 1];
        if (key.includes(':user:')) return parts[parts.indexOf('user') + 1];
        return '';
    };

    const getUserInfo = (userId) => {
        const user = users.find(u => u.userId === userId);
        return user || { name: 'Unknown', email: userId };
    };

    const getFlagName = (flagId) => {
        const flag = KNOWN_FLAGS.find(f => f.id === flagId);
        return flag?.name || flagId;
    };

    const getTierName = (tierId) => {
        const tier = tiers.find(t => t.id === tierId);
        return tier?.name || tierId;
    };

    const getTierOverridesForTier = () => {
        if (!selectedTier) return [];
        return flagsData.tierOverrides.filter(o => o.key.includes(`:tier:${selectedTier}`));
    };

    // Merge known flags with any other flags found in global defaults
    const getAvailableFlags = () => {
        const flags = [...KNOWN_FLAGS];
        const knownIds = new Set(KNOWN_FLAGS.map(f => f.id));

        flagsData.global.forEach(flag => {
            const id = extractFlagId(flag.key);
            if (id && !knownIds.has(id)) {
                flags.push({
                    id,
                    name: id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, ' '),
                    description: 'Dynamically found'
                });
                knownIds.add(id);
            }
        });

        // Sort by name
        return flags.sort((a, b) => a.name.localeCompare(b.name));
    };

    const availableFlags = getAvailableFlags();

    // Flag selector component
    const FlagSelector = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
                <InputLabel>Feature Flag</InputLabel>
                <Select
                    value={selectedFlag}
                    label="Feature Flag"
                    onChange={(e) => setSelectedFlag(e.target.value)}
                >
                    {availableFlags.map((flag) => (
                        <MenuItem key={flag.id} value={flag.id}>
                            {flag.name} - {flag.description}
                        </MenuItem>
                    ))}
                    <MenuItem value="custom">+ Custom flag...</MenuItem>
                </Select>
            </FormControl>
            {selectedFlag === 'custom' && (
                <TextField
                    label="Custom Flag ID"
                    value={customFlagId}
                    onChange={(e) => setCustomFlagId(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="my_new_feature"
                    helperText="Enter a unique identifier (no spaces)"
                />
            )}
            <TextField
                label="Value"
                value={flagValue}
                onChange={(e) => setFlagValue(e.target.value)}
                fullWidth
                size="small"
                placeholder="true, false, or any text"
                helperText="Use 'true'/'false' for toggles, or any text for config values"
            />
        </Box>
    );

    if (loading && flagsData.global.length === 0) {
        return (
            <Box className="admin-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="admin-tab-content">
            {/* Header */}
            <Box className="admin-section-header">
                <Typography variant="h6">Feature Flags</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setHelpDialog(true)}
                        startIcon={<HelpOutlineIcon />}
                        size="small"
                    >
                        Help
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={loadData}
                        startIcon={<RefreshIcon />}
                        size="small"
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Priority:</strong> User Override → Tier Override → Global Default
            </Alert>

            {/* Global Flags */}
            <Accordion defaultExpanded className="admin-accordion">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="600">
                        🌍 Global Defaults ({flagsData.global.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            size="small"
                            onClick={() => { resetForm(); setAddGlobalDialog(true); }}
                        >
                            Add Flag
                        </Button>
                    </Box>
                    <TableContainer component={Paper} className="admin-table-container">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Flag</TableCell>
                                    <TableCell>Value</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {flagsData.global.map((flag) => {
                                    const flagId = extractFlagId(flag.key);
                                    const isBoolean = flag.value === 'true' || flag.value === 'false';
                                    return (
                                        <TableRow key={flag.key}>
                                            <TableCell>
                                                <Typography fontWeight="500">{getFlagName(flagId)}</Typography>
                                                <Typography variant="caption" color="textSecondary">{flag.key}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {isBoolean ? (
                                                    <Switch
                                                        checked={flag.value === 'true'}
                                                        onChange={(e) => handleSetFlag(flag.key, e.target.checked ? 'true' : 'false', flag.description)}
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Chip label={flag.value} size="small" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => handleDeleteFlag(flag.key)} color="error">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* Tier Overrides */}
            <Accordion className="admin-accordion">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="600">
                        📦 Tier Overrides ({flagsData.tierOverrides.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Select Tier</InputLabel>
                            <Select
                                value={selectedTier}
                                label="Select Tier"
                                onChange={(e) => setSelectedTier(e.target.value)}
                            >
                                {tiers.map((tier) => (
                                    <MenuItem key={tier.id} value={tier.id}>{tier.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {selectedTier && (
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                size="small"
                                onClick={() => { resetForm(); setAddTierDialog(true); }}
                            >
                                Add Override
                            </Button>
                        )}
                    </Box>

                    {selectedTier ? (
                        <TableContainer component={Paper} className="admin-table-container">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Flag</TableCell>
                                        <TableCell>Value</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {getTierOverridesForTier().length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                <Typography color="textSecondary">No overrides for {getTierName(selectedTier)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        getTierOverridesForTier().map((flag) => {
                                            const flagId = extractFlagId(flag.key);
                                            const isBoolean = flag.value === 'true' || flag.value === 'false';
                                            return (
                                                <TableRow key={flag.key}>
                                                    <TableCell>{getFlagName(flagId)}</TableCell>
                                                    <TableCell>
                                                        {isBoolean ? (
                                                            <Switch
                                                                checked={flag.value === 'true'}
                                                                onChange={(e) => handleSetFlag(flag.key, e.target.checked ? 'true' : 'false', flag.description)}
                                                                size="small"
                                                            />
                                                        ) : (
                                                            <Chip label={flag.value} size="small" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton size="small" onClick={() => handleDeleteFlag(flag.key)} color="error">
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info">Select a tier to manage overrides</Alert>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* User Overrides */}
            <Accordion className="admin-accordion">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="600">
                        👤 User Overrides ({flagsData.userOverrides.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Highest priority!</strong> Grant specific users access to features (e.g., admin beta access)
                    </Alert>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={<PersonIcon />}
                            size="small"
                            onClick={() => { resetForm(); setAddUserDialog(true); }}
                        >
                            Add User Override
                        </Button>
                    </Box>
                    <TableContainer component={Paper} className="admin-table-container">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Flag</TableCell>
                                    <TableCell>Value</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {flagsData.userOverrides.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Typography color="textSecondary">No user overrides</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    flagsData.userOverrides.map((flag) => {
                                        const flagId = extractFlagId(flag.key);
                                        const userId = extractScopeId(flag.key);
                                        const userInfo = getUserInfo(userId);
                                        return (
                                            <TableRow key={flag.key}>
                                                <TableCell>
                                                    <Typography fontWeight="500">{userInfo.name || userInfo.email}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{userInfo.email}</Typography>
                                                </TableCell>
                                                <TableCell>{getFlagName(flagId)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={flag.value}
                                                        color={flag.value === 'true' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton size="small" onClick={() => handleDeleteFlag(flag.key)} color="error">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* Add Global Dialog */}
            <Dialog open={addGlobalDialog} onClose={() => setAddGlobalDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Global Flag</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <FlagSelector />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddGlobalDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddGlobal} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>

            {/* Add Tier Override Dialog */}
            <Dialog open={addTierDialog} onClose={() => setAddTierDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Tier Override for {getTierName(selectedTier)}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <FlagSelector />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddTierDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddTierOverride} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>

            {/* Add User Override Dialog */}
            <Dialog open={addUserDialog} onClose={() => setAddUserDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add User Override</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => `${option.name || option.email} (${option.email})`}
                            value={selectedUser}
                            onChange={(e, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Search User" size="small" placeholder="Type name or email..." />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.userId}>
                                    <Box>
                                        <Typography fontWeight="500">{option.name || 'No name'}</Typography>
                                        <Typography variant="caption" color="textSecondary">{option.email}</Typography>
                                    </Box>
                                </li>
                            )}
                        />
                        <FlagSelector />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddUserDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddUserOverride} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>

            {/* Help Dialog */}
            <Dialog open={helpDialog} onClose={() => setHelpDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>How to Use Feature Flags</DialogTitle>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Resolution Priority</Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        When checking a flag, the system looks for overrides in this order:
                        <ol style={{ margin: '8px 0 0 0' }}>
                            <li><strong>User Override</strong> - Specific to one user (highest priority)</li>
                            <li><strong>Tier Override</strong> - Applies to all users in a subscription tier</li>
                            <li><strong>Global Default</strong> - Applies to everyone (lowest priority)</li>
                        </ol>
                    </Alert>

                    <Typography variant="h6" gutterBottom>Backend Usage</Typography>
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#1e1e1e' }}>
                        <pre style={{ color: '#9cdcfe', margin: 0, fontSize: '0.85rem' }}>
                            {`// Check if feature enabled for user (with overrides)
if (featureFlagService.isEnabledForUser(userId, "experimental")) {
    // Feature is enabled
}

// Get all resolved flags for frontend
Map<String, Object> flags = featureFlagService.getResolvedFlags(userId);`}
                        </pre>
                    </Paper>

                    <Typography variant="h6" gutterBottom>Frontend Usage</Typography>
                    <Paper sx={{ p: 2, bgcolor: '#1e1e1e' }}>
                        <pre style={{ color: '#9cdcfe', margin: 0, fontSize: '0.85rem' }}>
                            {`import { useFeatureFlags } from '../../context/FeatureFlagContext';

const MyComponent = () => {
    const { flags } = useFeatureFlags();
    return flags.experimental && <BetaFeature />;
};`}
                        </pre>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpDialog(false)} variant="contained">Got it</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FeatureFlagsTab;
